import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import Sunburst from 'sunburst-chart'

import * as transformers from './transformers'

const WIDTH_PADDING_PX = 20
const HEIGHT_PADDING_PX = 20

/**
 * Based on a couple manually measured examples, this seemed to be about average
 */
const EXPECTED_PX_PER_CHAR = 5.5
/**
 * __NOTE:__ this only applies for labels that are being truncated due to
 * overflow
 */
const MIN_LABEL_PADDING_PX = 10

const DEFAULT_COLOR = ds.color.Scale.PRIMARY.lightest

@ngCore.Component({
  selector: 'atn-ds-sunburst-chart',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /**
   * {@link ents.core.display.chart.Type.Sunburst Sunburst chart} being displayed
   */
  public chartSg = ngCore.input.required<
    ents.core.display.chart.Chart<ents.core.display.chart.Type.Sunburst>
  >({
    alias: 'chart',
  })

  /** @see {@link _chartElementSg} */
  private readonly _chartElementRefSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLElement>>('chart')
  /**
   * HTML element where the sunburst chart is added
   */
  private readonly _chartElementSg = ngCore.computed(
    (elementRef = this._chartElementRefSg()) => elementRef?.nativeElement,
  )

  /**
   * @see {@link Sunburst}
   */
  private readonly _sunburstSg = ngCore.computed(
    (chartElement = this._chartElementSg()) => {
      if (!chartElement) {
        // No chart element => no where to put sunburst
        return undefined
      }
      const sunburst = new Sunburst(chartElement)
        .radiusScaleExponent(1)
        .size(node => (node.children?.length ? undefined : (node.weight ?? 1)))
        .color(node =>
          node.level === 0
            ? DEFAULT_COLOR
            : (ds.color.Scale.ALL[node.slice].tint(node.level - 1) ??
              DEFAULT_COLOR),
        )
        .label(node => node.name ?? '')
        .labelOrientation('angular')
        .handleNonFittingLabel((label, availablePx, node) => {
          availablePx -= MIN_LABEL_PADDING_PX
          const expectedPx = EXPECTED_PX_PER_CHAR * label.length
          if (expectedPx > availablePx) {
            const maxCharacters = Math.floor(availablePx / EXPECTED_PX_PER_CHAR)
            if (maxCharacters < 3) {
              // If there isn't even room for an ellipsis, we just leave it
              // blank
              return false
            }
            // By default, long labels use an ellipsis
            return `${label.substring(0, maxCharacters - 3)}...`
          }
          // This should never happen because this function is only called when
          // it is too long, but in case our calculations show there is enough
          // room, we just return the whole label
          return label
        })
        .tooltipTitle(node => node.name ?? '')
        .tooltipContent(node => node.description ?? '')

      /** Handles sizing */
      function fitSunburstToChartElement() {
        if (!chartElement) {
          // No chart element => can't get size info
          return
        }
        sunburst.width(chartElement.clientWidth - WIDTH_PADDING_PX)
        sunburst.height(chartElement.clientHeight - HEIGHT_PADDING_PX)
      }
      // Handles resizes (registered first to avoid a race condition where size
      // change is missed between initial sizing and registering resize handler)
      chartElement.onresize = fitSunburstToChartElement
      // Once at the beggining
      fitSunburstToChartElement()

      return sunburst
    },
  )

  /**
   * Sets {@link Sunburst.data}
   */
  private readonly _dataSetter = ngCore.effect(() => {
    const sunburst = this._sunburstSg()
    const chart = this.chartSg()
    const root = transformers.getRootNode(chart.data)
    if (!sunburst || !root) {
      // No sunburst or no data => nothing to do
      return
    }
    sunburst.data(root)
  })
}
